import { getSurahSlug, getSurahInfo } from './surahs'
import { SURAH_NAMES_FR } from './surahNamesFr'

export type SupportedLang = 'en' | 'fr'

/** Production origin. Kept as a constant so tests can compare without
 *  reading process.env, and so the build does not depend on a runtime
 *  env var (the prerender script must produce identical output every
 *  time it runs against the same git SHA). */
export const SITE_ORIGIN = 'https://qlearn.smndiaye.com'

export const SUPPORTED_LANGS: readonly SupportedLang[] = ['en', 'fr'] as const

function assertLang(lang: string): asserts lang is SupportedLang {
  if (lang !== 'en' && lang !== 'fr') {
    throw new Error(`Unsupported language for SEO URL: ${lang}`)
  }
}

/** Canonical PATH (no origin) for a surah in the given language. Use this for
 *  in-app anchors: a `<Link to>` / `href` must be same-origin relative so it
 *  stays a real crawlable link without hard-coding the production host into
 *  every internal navigation. `buildSurahUrl` is the absolute form, for
 *  canonical/hreflang/og tags. */
export function buildSurahPath(lang: SupportedLang, surahNumber: number): string {
  assertLang(lang)
  const slug = getSurahSlug(surahNumber)
  return `/${lang}/surah/${surahNumber}/${slug}`
}

/** Absolute canonical URL for a surah in the given language. */
export function buildSurahUrl(lang: SupportedLang, surahNumber: number): string {
  return `${SITE_ORIGIN}${buildSurahPath(lang, surahNumber)}`
}

/** Canonical PATH of the per-language surah index — the crawl hub that links
 *  to all 114 surahs. No trailing slash: the CloudFront edge function 301s
 *  `/<lang>/surah/` to this form so exactly one URL is canonical. */
export function buildSurahIndexPath(lang: SupportedLang): string {
  assertLang(lang)
  return `/${lang}/surah`
}

/** Absolute URL of the per-language surah index. */
export function buildSurahIndexUrl(lang: SupportedLang): string {
  return `${SITE_ORIGIN}${buildSurahIndexPath(lang)}`
}

/** `<title>` for the surah index page. */
export function buildSurahIndexTitle(lang: SupportedLang): string {
  assertLang(lang)
  if (lang === 'fr') {
    return 'Les 114 sourates du Coran · Lire et écouter en ligne'
  }
  return 'The 114 Surahs of the Quran · Read & listen online'
}

/** `<meta name="description">` for the surah index page. */
export function buildSurahIndexDescription(lang: SupportedLang): string {
  assertLang(lang)
  if (lang === 'fr') {
    return "Parcourez les 114 sourates du Saint Coran. Lisez chaque sourate en arabe avec la traduction française, verset par verset, avec récitation audio et étude mot à mot."
  }
  return 'Browse all 114 surahs of the Holy Quran. Read each surah in Arabic with English translation, verse by verse, with audio recitation and word-by-word study.'
}

/** Returns the canonical path-only URL to redirect to when the user's
 *  `:slug` URL parameter doesn't match the canonical slug. Returns null
 *  when the slug matches (no redirect needed). */
export function getCanonicalRedirect(
  lang: SupportedLang,
  surahNumber: number,
  providedSlug: string | undefined
): string | null {
  assertLang(lang)
  const canonical = getSurahSlug(surahNumber)
  if (providedSlug === canonical) return null
  return `/${lang}/surah/${surahNumber}/${canonical}`
}

function ordinalEnglish(n: number): string {
  const mod100 = n % 100
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`
  const mod10 = n % 10
  if (mod10 === 1) return `${n}st`
  if (mod10 === 2) return `${n}nd`
  if (mod10 === 3) return `${n}rd`
  return `${n}th`
}

function getLocalizedName(lang: SupportedLang, surahNumber: number): string {
  const info = getSurahInfo(surahNumber)
  if (!info) throw new Error(`Unknown surah ${surahNumber}`)
  if (lang === 'fr') {
    return SURAH_NAMES_FR[surahNumber] ?? info.englishName
  }
  return info.englishName
}

export function buildPageTitle(lang: SupportedLang, surahNumber: number): string {
  assertLang(lang)
  const info = getSurahInfo(surahNumber)
  if (!info) throw new Error(`Unknown surah ${surahNumber}`)
  if (lang === 'fr') {
    return `${info.name} · Sourate ${surahNumber} du Coran · Arabe + traduction française`
  }
  return `${info.name} · Surah ${surahNumber} of the Quran · Arabic + English translation`
}

export function buildPageDescription(
  lang: SupportedLang,
  surahNumber: number,
  numberOfAyahs: number,
  revelationType: 'Meccan' | 'Medinan'
): string {
  assertLang(lang)
  const info = getSurahInfo(surahNumber)
  if (!info) throw new Error(`Unknown surah ${surahNumber}`)
  const localizedName = getLocalizedName(lang, surahNumber)
  if (lang === 'fr') {
    const ordinalFr = `${surahNumber}ème`
    const revFr = revelationType === 'Medinan' ? 'médinoise' : 'mecquoise'
    return `Lisez la sourate ${info.name} (${localizedName}), ${ordinalFr} sourate du Coran, en arabe avec traduction française. ${numberOfAyahs} versets, ${revFr}.`
  }
  const ordinal = ordinalEnglish(surahNumber)
  return `Read Surah ${info.name} (${localizedName}), the ${ordinal} surah of the Quran, in Arabic with English translation. ${numberOfAyahs} ayahs, ${revelationType}.`
}
